import { useEffect, useState } from "react";
import { isProduction } from "../config/environment";
import { decodeTokenService } from "../services/decodeTokenService";
import { userExperimentsService } from "../services/userExperiments/userExperimentsService";

const MASTERSHOP_FLAG = "mastershop";

const cachedEnabledByOwner: Record<string, boolean> = {};
const inFlightRequestByOwner: Record<string, Promise<boolean> | undefined> = {};

// Fuera de produccion (dev y local) Mastershop esta siempre ON, sin pasar por Flagr:
// asi se puede probar sin administrar experiments por ambiente. En produccion manda
// el experiment `mastershop` por usuario, igual que el backend y el builder.
const isNonProductionEnabled = (): boolean => !isProduction;

const isEnabledValue = (value: unknown): boolean => {
  if (value === true) return true;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return false;
};

const loadMastershopFlag = async (ownerId: string): Promise<boolean> => {
  if (cachedEnabledByOwner[ownerId] !== undefined) {
    return cachedEnabledByOwner[ownerId];
  }

  if (!inFlightRequestByOwner[ownerId]) {
    inFlightRequestByOwner[ownerId] = userExperimentsService
      .getExperiments()
      .then((response) => {
        const enabled = isEnabledValue(response.experiments?.[MASTERSHOP_FLAG]?.enabled);
        cachedEnabledByOwner[ownerId] = enabled;
        return enabled;
      })
      .catch((error) => {
        console.error("Error fetching mastershop flag:", error);
        // FAIL-SAFE: ante error de Flagr, Mastershop queda oculto.
        cachedEnabledByOwner[ownerId] = false;
        return false;
      })
      .finally(() => {
        inFlightRequestByOwner[ownerId] = undefined;
      });
  }

  return inFlightRequestByOwner[ownerId];
};

export const useMastershopFlag = () => {
  const ownerId = decodeTokenService.getUserIdFromToken();
  const enabledByEnvironment = isNonProductionEnabled();
  const [enabled, setEnabled] = useState(() =>
    enabledByEnvironment || (ownerId ? cachedEnabledByOwner[ownerId] === true : false)
  );

  useEffect(() => {
    let active = true;

    if (enabledByEnvironment) {
      setEnabled(true);
      return () => {
        active = false;
      };
    }

    if (!ownerId) {
      setEnabled(false);
      return () => {
        active = false;
      };
    }

    loadMastershopFlag(ownerId).then((nextEnabled) => {
      if (!active) return;
      setEnabled(nextEnabled);
    });

    return () => {
      active = false;
    };
  }, [enabledByEnvironment, ownerId]);

  return enabled;
};
